// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import AggregationSelect from './AggregationSelect';
import {
  StyledAggregationSection,
  StyledAggregationCriteriaRow,
  StyledAggregationClause,
  StyledAggregationNumberInput,
} from './styles';
import { AggregationEditorContext } from './context';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import {
  SCOPE_OPTIONS,
  TIMEFRAME_UNIT_OPTIONS,
  getTimeframeMax,
} from './constants';
import type { AggregationSelectFieldKey } from './types';
import {
  buildOptionGroups,
  getColumnKey,
  getAggregationTypeOptions,
  reconcileAggregationType,
  toColumnOptions,
} from './helpers';
import type { Option } from '@usealloy/component-library';
import type { AggregationType, TimeframeUnit } from 'flow/store/engine/constants';

const BaseAggregationsSection = () => {
  const {
    sourceData, setSourceData, multiSelectProps, aggregationConfig,
  } = useContext(AggregationEditorContext);
  const { containerType } = useContext(RuleBuilderContext);

  const columnSlugOptions = useMemo(
    () => toColumnOptions(aggregationConfig.columns),
    [aggregationConfig.columns],
  );
  const columnSlugOptionsGrouped = useMemo(
    () => buildOptionGroups(columnSlugOptions, aggregationConfig.objectDisplayName),
    [columnSlugOptions, aggregationConfig.objectDisplayName],
  );

  const columnKey = getColumnKey(sourceData.aggregation);

  const aggregationTypeOptions = useMemo(
    () => getAggregationTypeOptions(columnSlugOptions, columnKey),
    [columnSlugOptions, columnKey],
  );
  const timeframeMax = useMemo(
    () => getTimeframeMax(sourceData.scope, sourceData.timeframeUnit),
    [sourceData.scope, sourceData.timeframeUnit],
  );

  const handleSelectChange = (field: AggregationSelectFieldKey) => (option: Option) => {
    const { value } = option;
    switch (field) {
      case 'timeframeUnit': {
        const newUnit = value as TimeframeUnit;
        setSourceData(prev => {
          const newMax = getTimeframeMax(prev.scope, newUnit);
          return {
            ...prev,
            timeframeUnit: newUnit,
            timeframeIntervalStart: Math.min(prev.timeframeIntervalStart, newMax),
            timeframeIntervalEnd: Math.min(prev.timeframeIntervalEnd, newMax),
          };
        });
        break;
      }
      case 'columnSlug': {
        const selected = columnSlugOptions.find(
          o => o.value === value,
        );
        const paMetadata = selected?.ui_data_type === 'published_attribute'
          ? {
            publishedAttributeToken: selected.value,
            publishedAttributeDescription:
              selected.published_attribute_description ?? null,
            publishedAttributeName: selected.published_attribute_name,
            publishedAttributeDataType: selected.data_type,
            containsPii: selected.contains_pii,
          }
          : undefined;
        setSourceData(prev => ({
          ...prev,
          aggregation: {
            columnSlug: selected?.column_slug ?? value,
            aggregationType: reconcileAggregationType(
              columnSlugOptions, value, prev.aggregation.aggregationType,
            ),
            ...(paMetadata && {
              publishedAttributeMetadata: paMetadata,
            }),
          },
        }));
        break;
      }
      case 'aggregationType':
        setSourceData(prev => ({
          ...prev,
          aggregation: {
            ...prev.aggregation,
            aggregationType: value as AggregationType,
          },
        }));
        break;
      case 'scope': {
        setSourceData(prev => {
          const newMax = getTimeframeMax(value, prev.timeframeUnit);
          return {
            ...prev,
            scope: value,
            timeframeIntervalStart: Math.min(prev.timeframeIntervalStart, newMax),
            timeframeIntervalEnd: Math.min(prev.timeframeIntervalEnd, newMax),
          };
        });
        break;
      }
    }
  };

  const handleTimeframeNumberChange = (field: 'timeframeIntervalStart' | 'timeframeIntervalEnd') => (newValue: number) => {
    setSourceData(prev => ({ ...prev, [field]: newValue }));
  };

  return (
    <StyledAggregationSection>
      <StyledAggregationCriteriaRow>
        <AggregationSelect
          id="agg-type"
          multiSelectProps={multiSelectProps}
          options={aggregationTypeOptions}
          value={sourceData.aggregation.aggregationType}
          onChange={handleSelectChange('aggregationType')}
        />
        <StyledAggregationClause containerType={containerType}>
          of
        </StyledAggregationClause>
        <AggregationSelect
          id="agg-column"
          multiSelectProps={multiSelectProps}
          options={columnSlugOptionsGrouped}
          value={columnKey}
          onChange={handleSelectChange('columnSlug')}
        />
        <StyledAggregationClause containerType={containerType}>
          with the following criteria:
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Scope:
        </StyledAggregationClause>
        <AggregationSelect
          id="agg-scope"
          multiSelectProps={multiSelectProps}
          options={SCOPE_OPTIONS}
          value={sourceData.scope}
          onChange={handleSelectChange('scope')}
        />
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Timeframe:
        </StyledAggregationClause>
        <StyledAggregationNumberInput
          minValue={0}
          maxValue={timeframeMax}
          value={sourceData.timeframeIntervalStart}
          onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalStart')}
        />
        <StyledAggregationClause containerType={containerType}>
          to
        </StyledAggregationClause>
        <StyledAggregationNumberInput
          minValue={sourceData.timeframeIntervalStart}
          maxValue={timeframeMax}
          value={sourceData.timeframeIntervalEnd}
          onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalEnd')}
        />
        <AggregationSelect
          id="agg-timeframe-unit"
          multiSelectProps={multiSelectProps}
          options={TIMEFRAME_UNIT_OPTIONS}
          value={sourceData.timeframeUnit}
          onChange={handleSelectChange('timeframeUnit')}
        />
        <StyledAggregationClause containerType={containerType}>
          ago
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>
    </StyledAggregationSection>
  );
};

export default BaseAggregationsSection;
